import {Box, Heading, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {append, includes, without} from 'ramda';

import i18n from '@dms/i18n';

import type {
  CustomerInsightsHistoryGroup,
  CustomerInsightsViewModel,
} from '../../../types/CustomerInsightsViewModel';
import {CustomerInsightsHistoryGroup as CustomerInsightsHistoryGroupView} from './CustomerInsightsHistoryGroup';
import {CustomerInsightsSection} from './CustomerInsightsSection';

const HISTORY_SKELETON_HEIGHT = 8;

type CustomerInsightsHistorySectionName =
  | 'pastSales'
  | 'pastPurchases'
  | 'unsuccessfulInterests'
  | 'serviceHistory';

export interface CustomerInsightsHistorySectionsProps {
  viewModel: Pick<
    CustomerInsightsViewModel,
    'pastSales' | 'pastPurchases' | 'unsuccessfulInterests' | 'serviceHistory'
  >;
}

export function CustomerInsightsHistorySections(props: CustomerInsightsHistorySectionsProps) {
  // Expansion lives above the groups because a group is re-created whenever its
  // section re-renders. Keeping it inside the group made loading a further page
  // collapse the group the user had just expanded.
  const [expandedSections, setExpandedSections] = useState<CustomerInsightsHistorySectionName[]>(
    []
  );
  const toggleExpandedSection = (name: CustomerInsightsHistorySectionName) =>
    setExpandedSections((current) =>
      includes(name, current) ? without([name], current) : append(name, current)
    );
  const historySections = [
    {name: 'pastSales' as const, section: props.viewModel.pastSales},
    {name: 'pastPurchases' as const, section: props.viewModel.pastPurchases},
    {name: 'unsuccessfulInterests' as const, section: props.viewModel.unsuccessfulInterests},
    {name: 'serviceHistory' as const, section: props.viewModel.serviceHistory},
  ];
  const showPersistentHistory = [
    props.viewModel.pastSales,
    props.viewModel.pastPurchases,
    props.viewModel.unsuccessfulInterests,
  ].some((section) =>
    match(section.status)
      .with('ready', 'degraded', 'empty', () => true)
      .otherwise(() => false)
  );
  const showServiceHistory = match(props.viewModel.serviceHistory.status)
    .with('ready', 'degraded', () => true)
    .otherwise(() => false);
  const showHistoryRows = showPersistentHistory || showServiceHistory;
  const hasHistoryLoading = historySections.some(({section}) => section.status === 'loading');
  const firstLoadingHistorySection = historySections.find(
    ({section}) => section.status === 'loading'
  )?.name;

  return (
    <Show when={showHistoryRows || hasHistoryLoading}>
      <Box backgroundColor="general.white">
        <Show when={showHistoryRows}>
          <Box paddingHorizontal={4} paddingVertical={2}>
            <Heading size={5} headingLevel={5}>
              {i18n.t('general.labels.history')}
            </Heading>
          </Box>
        </Show>
        <CustomerInsightsSection<CustomerInsightsHistoryGroup>
          section={props.viewModel.pastSales}
          title={i18n.t('feature.customerInsights.pastSales')}
          skeletonHeight={HISTORY_SKELETON_HEIGHT}
          showSkeleton={showHistoryRows || firstLoadingHistorySection === 'pastSales'}
          data-testid="customerInsights-pastSales"
          emptyFallback={
            <CustomerInsightsHistoryGroupView
              title={i18n.t('feature.customerInsights.pastSales')}
              group={{total: 0, items: []}}
              kind="sales"
              isExpanded={false}
              onToggleExpanded={() => toggleExpandedSection('pastSales')}
            />
          }
        >
          {(group) => (
            <CustomerInsightsHistoryGroupView
              title={i18n.t('feature.customerInsights.pastSales')}
              group={group}
              kind="sales"
              isExpanded={includes('pastSales', expandedSections)}
              onToggleExpanded={() => toggleExpandedSection('pastSales')}
            />
          )}
        </CustomerInsightsSection>
        <CustomerInsightsSection<CustomerInsightsHistoryGroup>
          section={props.viewModel.pastPurchases}
          title={i18n.t('feature.customerInsights.pastPurchases')}
          skeletonHeight={HISTORY_SKELETON_HEIGHT}
          showSkeleton={showHistoryRows || firstLoadingHistorySection === 'pastPurchases'}
          data-testid="customerInsights-pastPurchases"
          emptyFallback={
            <CustomerInsightsHistoryGroupView
              title={i18n.t('feature.customerInsights.pastPurchases')}
              group={{total: 0, items: []}}
              kind="purchases"
              isExpanded={false}
              onToggleExpanded={() => toggleExpandedSection('pastPurchases')}
            />
          }
        >
          {(group) => (
            <CustomerInsightsHistoryGroupView
              title={i18n.t('feature.customerInsights.pastPurchases')}
              group={group}
              kind="purchases"
              isExpanded={includes('pastPurchases', expandedSections)}
              onToggleExpanded={() => toggleExpandedSection('pastPurchases')}
            />
          )}
        </CustomerInsightsSection>
        <CustomerInsightsSection<CustomerInsightsHistoryGroup>
          section={props.viewModel.unsuccessfulInterests}
          title={i18n.t('feature.customerInsights.unsuccessfulInterests')}
          skeletonHeight={HISTORY_SKELETON_HEIGHT}
          showSkeleton={showHistoryRows || firstLoadingHistorySection === 'unsuccessfulInterests'}
          data-testid="customerInsights-unsuccessfulInterests"
          emptyFallback={
            <CustomerInsightsHistoryGroupView
              title={i18n.t('feature.customerInsights.unsuccessfulInterests')}
              group={{total: 0, items: []}}
              kind="lost"
              isExpanded={false}
              onToggleExpanded={() => toggleExpandedSection('unsuccessfulInterests')}
            />
          }
        >
          {(group) => (
            <CustomerInsightsHistoryGroupView
              title={i18n.t('feature.customerInsights.unsuccessfulInterests')}
              group={group}
              kind="lost"
              isExpanded={includes('unsuccessfulInterests', expandedSections)}
              onToggleExpanded={() => toggleExpandedSection('unsuccessfulInterests')}
            />
          )}
        </CustomerInsightsSection>
        <CustomerInsightsSection<CustomerInsightsHistoryGroup>
          section={props.viewModel.serviceHistory}
          title={i18n.t('feature.customerInsights.serviceHistory')}
          skeletonHeight={HISTORY_SKELETON_HEIGHT}
          showSkeleton={showHistoryRows || firstLoadingHistorySection === 'serviceHistory'}
          data-testid="customerInsights-serviceHistory"
        >
          {(group) => (
            <CustomerInsightsHistoryGroupView
              title={i18n.t('feature.customerInsights.serviceHistory')}
              group={group}
              kind="service"
              isExpanded={includes('serviceHistory', expandedSections)}
              onToggleExpanded={() => toggleExpandedSection('serviceHistory')}
            />
          )}
        </CustomerInsightsSection>
      </Box>
    </Show>
  );
}
